/**
 * Preferences type picker — user selects which fund preference categories to configure.
 *
 * Belongs here: list UI and navigation into full questionnaire.
 * Does not belong here: live Q flow (see PreferencesQuestionnairePage).
 */
import { useEffect } from 'react'
import { useExpertReviewNavigation } from '../../navigation/expertReviewNavigation'
import { EXPERT_REVIEW_CAME_FROM } from '../../navigation/expertReviewNavigation'
import { PrimaryButton } from '../../../../../components/execution/PrimaryButton'
import { usePreferencesContext } from '../flow-engine/PreferencesProvider'
import { getBottomPadding } from '../../../../../utils/sessionStorage'
import { EventNames } from '../../../../../types/misc.types'
import { expertReviewSafeAreaTopStyle } from '../../shared/expertReviewSafeArea'
import { getPreferenceTypeIcon } from '../ui/getPreferenceTypeIcon'
import type { FundPreferenceType } from '../setup/types'
import { ImageAvailability } from '../../shared/types/common.types'
import { ExpertReviewTopBar } from '../../shared/components/ExpertReviewTopBar'
import { ExpertReviewEventIds, ExpertReviewScreen } from '../../analytics/expertReviewEvents'
import {
  DirectionalSlideSwap,
  SlideDirection
} from '../../shared/transitions/DirectionalSlideSwap'
import { lightImpactVibrate, trackEvent } from '../../../../../utils/appBridge'

function PreferenceTypeRow({ preferenceType }: { preferenceType: FundPreferenceType }) {
  const { preferenceTypeIconAvailabilityById } = usePreferencesContext()
  const fundCount = preferenceType?.fundList?.length ?? 0
  const fundLabel = fundCount === 0 ? 'No funds detected' : `${fundCount} fund${fundCount > 1 ? 's' : ''}`
  const PreferenceTypeIcon = getPreferenceTypeIcon(
    preferenceType.id,
    preferenceTypeIconAvailabilityById[preferenceType.id] ?? ImageAvailability.UNKNOWN
  )
  return (
    <div
      className="flex items-center gap-4 bg-white p-4 rounded-[24px]"
    >
      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center text-gray-500" aria-hidden>
        <PreferenceTypeIcon className="h-6 w-6" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-[500] text-[#232323]" test-locator={`preferences-list-type-${preferenceType.id}`}>{preferenceType.name}</p>
        <p className="text-[12px] font-[400] text-[#6A6E6E]">
          {fundLabel}
        </p>
      </div>
    </div>
  )
}

export default function AllocationPreferencesListPage() {
  const { goBackFromPreferencesList } = useExpertReviewNavigation()
  const {
    preferenceTypes,
    isPreferenceTypesLoading,
    isPreferenceTypesError,
    beginEditAll
  } = usePreferencesContext()

  const handleBack = () => goBackFromPreferencesList()
  const handleContinue = () => {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: ExpertReviewScreen.PreferencesList,
        eventId: ExpertReviewEventIds.PreferencesContinue,
        buttonLabel: 'Continue'
      }
    })
    beginEditAll({ cameFrom: EXPERT_REVIEW_CAME_FROM.PREFERENCES_LIST })
  }

  const isListReady =
    !isPreferenceTypesLoading && !isPreferenceTypesError && preferenceTypes.length > 0

  useEffect(() => {
    if (!isListReady) return
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: { ScreenName: ExpertReviewScreen.PreferencesList }
    })
  }, [isListReady])

  if (isPreferenceTypesLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white px-5">
        <p className="text-gray-600">Loading…</p>
      </div>
    )
  }

  if (isPreferenceTypesError || !preferenceTypes.length) {
    return (
      <div
        className="h-screen bg-white px-5 py-6 flex flex-col"
        style={{
          ...expertReviewSafeAreaTopStyle(),
          paddingBottom: `calc(${getBottomPadding()}px + 16px)`
        }}
      >
        <header className="shrink-0">
          <ExpertReviewTopBar
            onBack={handleBack}
            menu="preferences"
            analyticsScreen={ExpertReviewScreen.PreferencesList}
            testLocatorBack="preferences-list-error-back"
            testLocatorMenu="preferences-list-error-menu"
          />
        </header>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-600">Failed to load preference types</p>
        </div>
      </div>
    )
  }

  return (
    <div
      className="h-screen bg-white px-5 flex flex-col min-h-0"
      style={{
        ...expertReviewSafeAreaTopStyle(),
        paddingBottom: `calc(${getBottomPadding()}px + 16px)`
      }}
    >
      <header className="shrink-0">
        <ExpertReviewTopBar
          onBack={handleBack}
          menu="preferences"
          analyticsScreen={ExpertReviewScreen.PreferencesList}
          testLocatorBack="preferences-list-back"
          testLocatorMenu="preferences-list-menu"
        />
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
        <DirectionalSlideSwap contentKey="preferences-list" direction={SlideDirection.Forward}>
          <h1 className="pt-4 text-[28px] font-[400] leading-[130%] text-[#232323]" test-locator="preferences-list-title">
            Additional preferences
          </h1>
          <p className="pt-3 text-[13px] font-[400] leading-[150%] text-[#6A6E6E] w-[95%]" test-locator="preferences-list-description">
            Answer a few questions to help our experts understand which funds to give recommendations on, and which ones to leave unchanged.
          </p>
          <section className="mt-6 flex flex-col py-1 border border-[#F0F4F7] rounded-[16px] overflow-hidden">
            {preferenceTypes.map((preferenceType) => (
              <PreferenceTypeRow key={preferenceType.id} preferenceType={preferenceType} />
            ))}
          </section>
        </DirectionalSlideSwap>
      </div>

      <div className="shrink-0 mt-8 w-full">
        <PrimaryButton
          testLocator="preferences-list-continue"
          label="Continue"
          onClick={handleContinue}
          primaryButtonClassName="w-full !h-[56px] !font-[500] !rounded-[16px]"
        />
      </div>
    </div>
  )
}
